import {
    autoUpdate,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    offset,
    Placement,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useInteractions,
} from "@floating-ui/react";
import { useState } from "react";

import styles from "./styles.module.scss";

export type DropdownProps = {
    trigger: React.ReactNode;
    children: React.ReactNode;
    placement?: Placement;
    matchWidth?: boolean;
    /** Contrôle l'ouverture (mode contrôlé). Si fourni, le parent gère l'état. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
};

export const Dropdown = ({
    trigger,
    children,
    placement = "bottom-start",
    open: controlledOpen,
    onOpenChange,
}: DropdownProps) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;

    const handleOpenChange = (open: boolean) => {
        if (!isControlled) setInternalOpen(open);
        onOpenChange?.(open);
    };

    const nodeId = useFloatingNodeId()!;

    const { refs, context } = useFloating({
        nodeId,
        open: isOpen,
        onOpenChange: handleOpenChange as (open: boolean) => void,
        whileElementsMounted: autoUpdate,
        middleware: [offset(6), shift()],
        placement,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context),
        useDismiss(context),
    ]);

    const floatingWidth = refs.reference.current
        ? (refs.reference.current as HTMLElement).offsetWidth
        : undefined;

    return (
        <FloatingTree>
            <div
                ref={(node) => refs.setReference(node)}
                data-popover={isOpen ? "open" : "closed"}
                {...getReferenceProps()}
                className={styles.trigger}
            >
                {trigger}
            </div>
            {isOpen && (
                <FloatingPortal>
                    <FloatingNode id={nodeId}>
                        <div
                            ref={(node) => refs.setFloating(node)}
                            style={{ 
                                ...context.floatingStyles, 
                                zIndex: 10000,
                                ...(floatingWidth ? { minWidth: floatingWidth } : {})
                            }}
                            {...getFloatingProps()}
                        >
                            {children}
                        </div>
                    </FloatingNode>
                </FloatingPortal>
            )}
        </FloatingTree>
    );
};
